import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Account {
  id: number;
  competitor: string;
  industry: string;
  dealStage: string;
  transcript: string;
}

interface Client {
  id: string;
  name: string;
}

interface Notification {
  id: number;
  topic: string;
  message: string;
}

interface Filters {
  competitor: string;
  industry: string;
  dealStage: string;
}

interface User {
  email: string;
  subscribedTopics: string[];
}

interface AppContextType {
  clients: Client[];
  selectedClient: Client | null;
  setSelectedClient: React.Dispatch<React.SetStateAction<Client | null>>;
  accounts: Account[];
  notifications: Notification[];
  filteredAccounts: Account[];
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  addAccount: (clientId: string, account: Omit<Account, 'id'>) => Promise<void>;
  sendOpenAIPrompt: (prompt: string) => Promise<string>;
  users: User[];
  addUser: (user: User) => void;
  updateUserSubscriptions: (email: string, subscribedTopics: string[]) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Mock data for initial clients and accounts
const mockClients: Client[] = [
  { id: 'client1', name: 'TechCorp' },
  { id: 'client2', name: 'FinanceHub' },
];

const mockAccounts: Account[] = [
  { id: 1, competitor: 'CompA', industry: 'Software', dealStage: 'Closed Won', transcript: 'The client was impressed with our AI-driven solutions.' },
  { id: 2, competitor: 'CompB', industry: 'Banking', dealStage: 'Closed Lost', transcript: 'Despite our competitive pricing, the client chose CompY due to their existing relationship.' },
];

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [clients, setClients] = useState<Client[]>(mockClients);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [accounts, setAccounts] = useState<Account[]>(mockAccounts);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filters, setFilters] = useState<Filters>({ competitor: '', industry: '', dealStage: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    // In a real application, you would fetch clients and accounts from an API here
    // For now, we'll use the mock data
    setClients(mockClients);
    setAccounts(mockAccounts);
  }, []);

  const filteredAccounts = accounts.filter(account => 
    (filters.competitor === '' || account.competitor === filters.competitor) &&
    (filters.industry === '' || account.industry === filters.industry) &&
    (filters.dealStage === '' || account.dealStage === filters.dealStage) &&
    (searchTerm === '' || 
      account.competitor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.industry.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.dealStage.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const addAccount = async (clientId: string, account: Omit<Account, 'id'>): Promise<void> => {
    const newAccount = { ...account, id: accounts.length + 1 };
    setAccounts(prev => [...prev, newAccount]);
  };

  const sendOpenAIPrompt = async (prompt: string): Promise<string> => {
    // Mock OpenAI response
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(`AI response to: ${prompt}`);
      }, 1000);
    });
  };

  const addUser = (user: User) => {
    setUsers(prevUsers => [...prevUsers, user]);
  };

  const updateUserSubscriptions = (email: string, subscribedTopics: string[]) => {
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.email === email ? { ...user, subscribedTopics } : user
      )
    );
  };

  const value = {
    clients,
    selectedClient,
    setSelectedClient,
    accounts,
    notifications,
    filteredAccounts,
    filters,
    setFilters,
    searchTerm,
    setSearchTerm,
    addAccount,
    sendOpenAIPrompt,
    users,
    addUser,
    updateUserSubscriptions,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};